import { useState } from 'react'
import { Modal } from '../ui/Modal'
import { useToast } from '../ui/Toast'

interface Props {
  open: boolean
  onClose: () => void
  siteId: string
  onCreate: (gw: {
    site_id: string
    name: string
    droplet_ip: string
    go2rtc_port?: number
    router_ip?: string
    tunnel_ip?: string
    carrier?: string
    sim_only?: boolean
  }) => Promise<unknown>
}

export function AddGatewayDialog({ open, onClose, siteId, onCreate }: Props) {
  const { toast } = useToast()
  const [name, setName] = useState('')
  const [dropletIp, setDropletIp] = useState('')
  const [go2rtcPort, setGo2rtcPort] = useState('1984')
  const [routerIp, setRouterIp] = useState('')
  const [tunnelIp, setTunnelIp] = useState('')
  const [carrier, setCarrier] = useState('')
  const [simOnly, setSimOnly] = useState(false)
  const [saving, setSaving] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await onCreate({
        site_id: siteId,
        name: name.trim(),
        droplet_ip: dropletIp.trim(),
        go2rtc_port: parseInt(go2rtcPort) || 1984,
        router_ip: routerIp.trim() || undefined,
        tunnel_ip: tunnelIp.trim() || undefined,
        carrier: carrier.trim() || undefined,
        sim_only: simOnly || undefined,
      })
      toast('Gateway added', 'success')
      setName(''); setDropletIp(''); setGo2rtcPort('1984')
      setRouterIp(''); setTunnelIp(''); setCarrier(''); setSimOnly(false)
      onClose()
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to add gateway', 'error')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'
  const labelCls = 'block text-xs uppercase tracking-wide text-gray-500 mb-1.5'

  return (
    <Modal open={open} onClose={onClose} title="Add Gateway">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelCls}>Name *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            placeholder="e.g. Router 1 — Front Building"
            className={inputCls}
            autoFocus
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Droplet IP *</label>
            <input
              type="text"
              value={dropletIp}
              onChange={e => setDropletIp(e.target.value)}
              required
              placeholder="e.g. 164.90.123.45"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Stream Port</label>
            <input
              type="number"
              value={go2rtcPort}
              onChange={e => setGo2rtcPort(e.target.value)}
              placeholder="1984"
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Router IP</label>
            <input
              type="text"
              value={routerIp}
              onChange={e => setRouterIp(e.target.value)}
              placeholder="e.g. 192.168.1.1"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Tunnel IP</label>
            <input
              type="text"
              value={tunnelIp}
              onChange={e => setTunnelIp(e.target.value)}
              placeholder="e.g. 10.8.0.2"
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Carrier</label>
            <input
              type="text"
              value={carrier}
              onChange={e => setCarrier(e.target.value)}
              placeholder="e.g. EE, Three, Vodafone"
              className={inputCls}
            />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={simOnly}
                onChange={e => setSimOnly(e.target.checked)}
                className="rounded border-gray-300"
              />
              SIM-only
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50"
          >
            {saving ? 'Adding...' : 'Add Gateway'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
